import { DeviceRegistry } from './deviceRegistry';
import { LoggerBridge, MIC_ACQUIRE_SETTLE_DELAY_MS, delay, isAndroid, isIOSSafari, isMobilePlatform } from './shared';

// What the Android diagnostics read from the engine's audio output.
export interface OutputDiagnostics {
    readonly state: string | null;
    readonly sinkId: string | null;
}

// Isolates every platform-specific behavior so the core flow stays platform-free.
// The desktop implementation doubles as the permissive base class.
export class DesktopQuirks {
    readonly name: string = 'desktop';
    // On mobile, enumerateDevices() exposes no real audiooutput entries — the OS owns routing.
    readonly allowsOutputSelection: boolean = true;
    // Android-only: during the post-switch cooldown even mic-loss events are downgraded,
    // because the Bluetooth HFP teardown/reconnect makes the just-selected mic momentarily vanish.
    readonly suppressAllDuringCooldown: boolean = false;

    constructor(protected readonly logger: LoggerBridge) { }

    async beforeMicAcquire(_isSwitch: boolean): Promise<void> { }
    async afterMicAcquire(_streamIsHeadset: boolean): Promise<void> { }
    async afterMicRelease(): Promise<void> { }

    excludeAsAutoSwitchTarget(_label: string): boolean {
        return false;
    }

    diagnosticCategory(): string | null {
        return null;
    }

    async logSwitchDiagnostics(_stage: string, _extra: Record<string, any>): Promise<void> { }
}

export type PlatformQuirks = DesktopQuirks;

export class GenericMobileQuirks extends DesktopQuirks {
    override readonly name: string = 'mobile';
    override readonly allowsOutputSelection: boolean = false;

    override async beforeMicAcquire(isSwitch: boolean): Promise<void> {
        // Let the previous device release fully before re-acquiring; only needed on switches.
        if (isSwitch) await delay(MIC_ACQUIRE_SETTLE_DELAY_MS);
    }
}

export class AndroidQuirks extends GenericMobileQuirks {
    override readonly name: string = 'android';
    override readonly suppressAllDuringCooldown: boolean = true;

    constructor(
        logger: LoggerBridge,
        private readonly registry: DeviceRegistry,
        private readonly output: OutputDiagnostics) {
        super(logger);
    }

    // Selecting the earpiece mic routes ALL output through the tiny top speaker.
    override excludeAsAutoSwitchTarget(label: string): boolean {
        return /earpiece/i.test(label);
    }

    override async logSwitchDiagnostics(stage: string, extra: Record<string, any>): Promise<void> {
        try {
            const snapshot = await this.registry.enumerate();
            await this.logger.info(`android.audioSwitch.${stage}`, {
                ...extra,
                contextState: this.output.state ?? 'null',
                contextSinkId: this.output.sinkId,
                audioInputDevices: snapshot.inputs,
                audioOutputDevices: snapshot.outputs
            });
        } catch { }
    }
}

// WebKit Bug 282939 / 211192: getUserMedia hijacks the audio output route on iOS Safari,
// routing it to the built-in speakerphone and ignoring AirPods or Bluetooth headsets.
//
// Sequence:
//   before-gum  → audioSession.type = 'auto'            (let iOS choose the current route freely)
//   after-gum   → audioSession.type = 'play-and-record' (headset streams only: lock the route)
//   cleanup     → audioSession.type = 'playback' → 'auto' (restore media volume fidelity)
//
// The 'after-gum' assignment is time-critical: any significant await between GUM resolution
// and this call risks the OS reverting the route before we lock it in.
export class IosSafariQuirks extends GenericMobileQuirks {
    override readonly name: string = 'ios-safari';
    private currentCategory: string | null = null;
    private streamIsHeadset = false;

    override async beforeMicAcquire(isSwitch: boolean): Promise<void> {
        await super.beforeMicAcquire(isSwitch);
        await this.applyPhase('before-gum');
    }

    override async afterMicAcquire(streamIsHeadset: boolean): Promise<void> {
        this.streamIsHeadset = streamIsHeadset;
        await this.applyPhase('after-gum');
    }

    override async afterMicRelease(): Promise<void> {
        await this.applyPhase('cleanup');
    }

    override diagnosticCategory(): string | null {
        return this.currentCategory;
    }

    private async applyPhase(phase: 'before-gum' | 'after-gum' | 'cleanup'): Promise<void> {
        const audioSession = (navigator as any).audioSession;
        // Guard: the API may not be present on older iOS versions
        if (!audioSession || typeof audioSession.type !== 'string') return;
        try {
            switch (phase) {
                case 'before-gum':
                    audioSession.type = 'auto';
                    this.currentCategory = 'auto';
                    await delay(20); // brief yield so iOS flushes the route change before GUM
                    break;
                case 'after-gum': {
                    // For built-in/speakerphone mics use 'auto' so iOS routes to the loudspeaker
                    // (its natural default). Only lock to 'play-and-record' for headset mics —
                    // that lock is what keeps iOS from hijacking the headset route back to speaker.
                    const sessionType = this.streamIsHeadset ? 'play-and-record' : 'auto';
                    audioSession.type = sessionType;
                    this.currentCategory = sessionType;
                    break;
                }
                case 'cleanup':
                    audioSession.type = 'playback';
                    this.currentCategory = 'playback';
                    await delay(50);
                    audioSession.type = 'auto';
                    this.currentCategory = 'auto';
                    break;
            }
            await this.logger.info('iOS audioSession phase applied', { phase, type: audioSession.type });
        } catch (e) {
            await this.logger.warning(`iOS audioSession failed (${phase})`, { error: String(e) });
        }
    }
}

export function createPlatformQuirks(
    logger: LoggerBridge,
    registry: DeviceRegistry,
    output: OutputDiagnostics): PlatformQuirks {
    if (isAndroid()) return new AndroidQuirks(logger, registry, output);
    if (isIOSSafari()) return new IosSafariQuirks(logger);
    if (isMobilePlatform()) return new GenericMobileQuirks(logger);
    return new DesktopQuirks(logger);
}
